// ══════════════════════════════════════════════════════════
// MANAGED STREAMING — Tipos para streaming administrado por RFW
//
// Define el contrato de aprovisionamiento entre el frontend
// y el backend PHP. El frontend solo lee estado y muestra UI.
// NUNCA crea cuentas, mounts, passwords, ni llama proveedores.
// ══════════════════════════════════════════════════════════

import type { StreamingMode } from '@/pages/register-radio/types';

// ── PROVISIONING STATUS ──

/**
 * Estados del pipeline de aprovisionamiento para RFW_MANAGED.
 *
 * NOT_CONFIGURED       → la radio no tiene modalidad de streaming definida.
 * EXTERNAL_STREAM      → la radio usa su propio streaming (no hay aprovisionamiento).
 * PENDING_PAYMENT      → plan contratado, esperando pago.
 * PENDING_APPROVAL     → pago confirmado, esperando aprobación administrativa.
 * PENDING_PROVISION    → aprobado, esperando que backend PHP cree la estación.
 * PROVISIONING         → backend PHP está creando la cuenta en el proveedor.
 * ACTIVE               → estación creada, stream_url confirmada, radio activa.
 * FAILED               → el aprovisionamiento falló (error sanitizado disponible).
 */
export type ManagedStreamingStatus =
  | 'NOT_CONFIGURED'
  | 'EXTERNAL_STREAM'
  | 'PENDING_PAYMENT'
  | 'PENDING_APPROVAL'
  | 'PENDING_PROVISION'
  | 'PROVISIONING'
  | 'ACTIVE'
  | 'FAILED';

// ── MANAGED STREAMING DETAILS ──

export interface ManagedStreamingDetails {
  /** Modalidad de streaming: EXTERNAL_STREAM o RFW_MANAGED */
  streamingMode: StreamingMode;
  /** Estado del pipeline de aprovisionamiento (solo RFW_MANAGED) */
  provisioningStatus: ManagedStreamingStatus;
  /** ID del plan contratado */
  planId: string | null;
  /** Nombre del plan contratado */
  planName: string | null;
  /** Precio del plan */
  planPrice: string | null;
  /** Funciones incluidas en el plan */
  planFeatures: string[];
  /** Si el plan incluye AutoDJ */
  autodjIncluded: boolean;
  /** Si el plan incluye streaming hosting */
  streamingHostingIncluded: boolean;
  /** Almacenamiento incluido (GB) — null si no aplica */
  storageGb: number | null;
  /** Bitrate del stream (kbps) — null si no está aprovisionado */
  bitrate: number | null;
  /** Listeners máximos — null si no está aprovisionado */
  listenersMax: number | null;
  /** Stream URL pública confirmada por el backend — null si no está aprovisionado */
  streamUrl: string | null;
  /** Fecha de solicitud de aprovisionamiento */
  requestedAt: string | null;
  /** Fecha del último intento de aprovisionamiento */
  lastAttemptAt: string | null;
  /** Error sanitizado del último intento fallido — nunca expone credenciales ni hosts */
  lastError: string | null;
  /** Si se puede reintentar el aprovisionamiento */
  canRetry: boolean;
  /** Para EXTERNAL_STREAM: si la URL está validada */
  externalStreamValidated: boolean | null;
  /** Para EXTERNAL_STREAM: URL pública autorizada */
  externalStreamUrl: string | null;
}

// ── LABELS ──

export const managedStreamingStatusLabels: Record<ManagedStreamingStatus, string> = {
  NOT_CONFIGURED: 'Sin configurar',
  EXTERNAL_STREAM: 'Streaming propio',
  PENDING_PAYMENT: 'Pago pendiente',
  PENDING_APPROVAL: 'Aprobación pendiente',
  PENDING_PROVISION: 'Aprovisionamiento pendiente',
  PROVISIONING: 'Aprovisionando',
  ACTIVE: 'Streaming activo',
  FAILED: 'Error',
};

export const managedStreamingStatusDescriptions: Record<ManagedStreamingStatus, string> = {
  NOT_CONFIGURED: 'No se ha definido la modalidad de streaming para esta radio.',
  EXTERNAL_STREAM: 'La radio transmite con su propio servidor de streaming externo.',
  PENDING_PAYMENT: 'El plan fue seleccionado. Esperando confirmación del pago para continuar.',
  PENDING_APPROVAL: 'El pago fue confirmado. El equipo de RFW está revisando la solicitud.',
  PENDING_PROVISION: 'La solicitud fue aprobada. El servidor de streaming será creado pronto.',
  PROVISIONING: 'El servidor de streaming se está configurando. Esto puede tomar unos minutos.',
  ACTIVE: 'El streaming administrado por RFW está operativo y transmitiendo.',
  FAILED: 'El aprovisionamiento no pudo completarse. Revisá el error e intentá de nuevo.',
};

export const managedStreamingStatusColors: Record<ManagedStreamingStatus, { dot: string; bg: string; text: string; border: string }> = {
  NOT_CONFIGURED:     { dot: 'bg-foreground-300', bg: 'bg-foreground-100', text: 'text-foreground-600', border: 'border-foreground-200' },
  EXTERNAL_STREAM:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  PENDING_PAYMENT:    { dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  PENDING_APPROVAL:   { dot: 'bg-sky-500', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  PENDING_PROVISION:  { dot: 'bg-secondary-500', bg: 'bg-secondary-50', text: 'text-secondary-700', border: 'border-secondary-200' },
  PROVISIONING:       { dot: 'bg-accent-500 animate-pulse', bg: 'bg-accent-50', text: 'text-accent-700', border: 'border-accent-200' },
  ACTIVE:             { dot: 'bg-emerald-500 animate-pulse', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  FAILED:             { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

export const managedStreamingStatusIcons: Record<ManagedStreamingStatus, string> = {
  NOT_CONFIGURED:     'ri-settings-3-line',
  EXTERNAL_STREAM:    'ri-plug-line',
  PENDING_PAYMENT:    'ri-bank-card-line',
  PENDING_APPROVAL:   'ri-shield-check-line',
  PENDING_PROVISION:  'ri-hourglass-line',
  PROVISIONING:       'ri-loader-4-line',
  ACTIVE:             'ri-broadcast-line',
  FAILED:             'ri-error-warning-line',
};

// ── REQUEST / RESPONSE TYPES ──

export interface ProvisioningRequest {
  radio_id: string;
  plan_id: string;
}

export interface ProvisioningResponse {
  radio_id: string;
  status: ManagedStreamingStatus;
  message: string;
  timestamp: string;
}

export interface ManagedStreamingStatusResponse {
  radio_id: string;
  streaming_mode: StreamingMode;
  provisioning_status: ManagedStreamingStatus;
  plan_id: string | null;
  plan_name: string | null;
  plan_price: string | null;
  plan_features: string[];
  autodj_included: boolean;
  streaming_hosting_included: boolean;
  storage_gb: number | null;
  bitrate: number | null;
  listeners_max: number | null;
  stream_url: string | null;
  requested_at: string | null;
  last_attempt_at: string | null;
  last_error: string | null;
  can_retry: boolean;
  external_stream_validated: boolean | null;
  external_stream_url: string | null;
}

// ── HELPERS ──

/** Determina si el estado permite reintentar el aprovisionamiento */
export function canRetryProvisioning(status: ManagedStreamingStatus): boolean {
  return status === 'FAILED' || status === 'NOT_CONFIGURED';
}

/** Determina si el estado es transitorio (el usuario debe esperar) */
export function isProvisioningTransient(status: ManagedStreamingStatus): boolean {
  return status === 'PENDING_PAYMENT' || status === 'PENDING_APPROVAL' || status === 'PENDING_PROVISION' || status === 'PROVISIONING';
}

/** Determina si el estado es terminal/positivo */
export function isProvisioningActive(status: ManagedStreamingStatus): boolean {
  return status === 'ACTIVE' || status === 'EXTERNAL_STREAM';
}

/** Determina si el estado requiere intervención */
export function isProvisioningActionable(status: ManagedStreamingStatus): boolean {
  return status === 'FAILED' || status === 'NOT_CONFIGURED';
}